import {
	CloseButton,
	Fieldset,
	List,
	LoadingOverlay,
	Modal,
	Skeleton,
	Space,
	Table,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconMinus, IconPlus } from '@tabler/icons-react';
import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { SmallModal } from '../../../components/SmallModal';
import { SmallPagination } from '../../../components/SmallPagination';
import { Title } from '../../../components/Title';
import { FormButtons } from '../../../components/form/FormButtons';
import { usePagination } from '../../../hooks/usePagination';
import { defaultIconStyle } from '../../../theme/styling/iconStyling';
import { mutate } from '../../../util/api/mutate';
import { undefinedCheck } from '../../../util/undefinedCheck';
import {
	useAvailableProductsQuery,
	useSelectedProductsQuery,
} from '../hooks/productPromotionQueries';
import type { PromotionProductDto } from '../types/PromotionDto';
import { ProductsList } from './ProductList';
import { KeyedRow } from '../../../components/KeyedRow';

interface NewPromotionModalProps {
	close: () => void;
	isOpened: boolean;
	promotionId: string;
	branchId: string;
	triggerListUpdate: () => void;
}

export function SelectProductsModal({
	isOpened,
	close,
	branchId,
	promotionId,
	triggerListUpdate,
}: NewPromotionModalProps) {
	const { organisation } = useParams();
	const [searchSelectedParam, setSearchSelectedParam] = useState('');
	const [searchAvailableParam, setSearchAvailableParam] = useState('');
	const pagination = usePagination(10);

	const [selectedProducts, setSelectedProducts] = useState<
		PromotionProductDto[]
	>([]);

	const selectedProductsQuery = useSelectedProductsQuery(
		branchId,
		promotionId,
		isOpened,
	);

	const { availableProductsQuery, availableProductsCountQuery } =
		useAvailableProductsQuery(
			branchId,
			isOpened,
			pagination.value,
			searchAvailableParam,
		);

	const updateProductsMutation = mutate<PromotionProductDto[]>(
		`${organisation}/promotions/update-products/${branchId}/${promotionId}`,
		selectedProducts,
	);

	useEffect(() => {
		setSelectedProducts(selectedProductsQuery.data?.value ?? []);
	}, [selectedProductsQuery.data]);

	const filteredSelectedProducts = useMemo(() => {
		if (searchSelectedParam.length > 0) {
			return selectedProducts.filter(
				(p) =>
					p.description
						.toLowerCase()
						.includes(searchSelectedParam.toLowerCase()) ||
					p.skuid.toLowerCase().includes(searchSelectedParam.toLowerCase()),
			);
		}

		return selectedProducts;
	}, [selectedProducts, searchSelectedParam]);

	const [
		showSuccessModal,
		{ open: openSuccessModal, close: closeSuccessModal },
	] = useDisclosure(false);

	const [
		showWarningModal,
		{ open: openWarningModal, close: closeWarningModal },
	] = useDisclosure(false);

	const closeAndReset = () => {
		availableProductsQuery.remove();
		availableProductsQuery.refetch();
		selectedProductsQuery.remove();
		selectedProductsQuery.refetch();
		updateProductsMutation.reset();

		setSearchAvailableParam('');
		setSearchSelectedParam('');

		closeSuccessModal();
		setSelectedProducts([]);

		close();
	};

	const isProductAlreadyUsed = (product: PromotionProductDto) => {
		return (
			product.promotionId !== promotionId &&
			product.promotionId !== '0' &&
			product.promotionId !== ''
		);
	};

	return (
		<>
			<Modal
				opened={isOpened}
				onClose={closeAndReset}
				title={<Title label={'Add Products'} />}
				w={100}
				withCloseButton={true}
				size={'xl'}
				centered
			>
				<SmallModal
					isOpen={showSuccessModal}
					close={closeAndReset}
					title={'Products Added!'}
					body={`All products have been added. There are now ${selectedProducts.length} products associated with this promotion...`}
				/>
				<SmallModal
					isOpen={showWarningModal}
					close={closeWarningModal}
					title={'Warning!'}
					body={
						<>
							{'These product(s) are already associated with a promotion:'}
							<List>
								<br />

								{selectedProducts
									.filter((p) => isProductAlreadyUsed(p))
									.map((p) => {
										// biome-ignore lint/correctness/useJsxKeyInIterable: <explanation>
										return <List.Item>{p.skuid}</List.Item>;
									})}
							</List>
							<br />
							{'They will be removed from their old promotion.'}
						</>
					}
					withCloseButton
					onComplete={async () => {
						closeWarningModal();

						await updateProductsMutation.mutateAsync().then(() => {
							openSuccessModal();
							triggerListUpdate();
						});
					}}
				/>
				<LoadingOverlay visible={updateProductsMutation.isLoading} />
				<Fieldset legend="Selected Products">
					<ProductsList
						searchQuery={searchSelectedParam}
						setSearchQuery={setSearchSelectedParam}
						productsQuery={selectedProductsQuery}
						showEmptyMessage={filteredSelectedProducts.length < 1}
						height="19vh"
						records={filteredSelectedProducts.map((product) => (
							<KeyedRow>
								<Table.Td>{undefinedCheck(product.skuid)}</Table.Td>
								<Table.Td>{undefinedCheck(product.description)}</Table.Td>
								<Table.Td>
									<CloseButton
										icon={<IconMinus color={'#8B0000'} {...defaultIconStyle} />}
										onClick={() =>
											setSelectedProducts((prevItems) =>
												prevItems.filter(
													(p: PromotionProductDto) => p.skuid !== product.skuid,
												),
											)
										}
									/>
								</Table.Td>
							</KeyedRow>
						))}
						emptyPlaceholder="No products selected..."
					/>
				</Fieldset>
				<Space h={20} />
				<Fieldset legend="Available Products">
					<ProductsList
						searchQuery={searchAvailableParam}
						setSearchQuery={setSearchAvailableParam}
						productsQuery={availableProductsQuery}
						emptyPlaceholder="No products associated with branch..."
						showEmptyMessage={
							(availableProductsQuery.data &&
								availableProductsQuery.data?.value.length < 1) ??
							false
						}
						filterContent={
							<Skeleton visible={availableProductsCountQuery.isLoading}>
								<SmallPagination
									pagination={pagination}
									totalItems={availableProductsCountQuery.data?.value ?? 10}
								/>
							</Skeleton>
						}
						records={availableProductsQuery.data?.value.map((product) => (
							// biome-ignore lint/correctness/useJsxKeyInIterable: <explanation>
							<KeyedRow>
								<Table.Td>{undefinedCheck(product.skuid)}</Table.Td>
								<Table.Td>{undefinedCheck(product.description)}</Table.Td>
								<Table.Td>
									<CloseButton
										disabled={selectedProducts.some(
											(s) => s.skuid === product.skuid,
										)}
										icon={
											<IconPlus
												color={
													selectedProducts.some(
														(s) => s.skuid === product.skuid,
													)
														? 'grey'
														: '#006400'
												}
												size={'1rem'}
											/>
										}
										onClick={() => setSelectedProducts((p) => [...p, product])}
									/>
								</Table.Td>
							</KeyedRow>
						))}
					/>
				</Fieldset>
				<FormButtons
					close={closeAndReset}
					onClick={async () => {
						if (selectedProducts.some((p) => isProductAlreadyUsed(p))) {
							openWarningModal();
						} else {
							await updateProductsMutation.mutateAsync().then(() => {
								openSuccessModal();
								triggerListUpdate();
							});
						}
					}}
				/>
			</Modal>
		</>
	);
}
